/**
 * AccordionRegister — the ONE open-row register for the whole product (Pass 1
 * shared component / ground rule: one row open at a time). A single key names
 * whatever row is open anywhere — a dashboard bank, an expense row, a Manage
 * row, a map place — so opening any row closes whatever was open, including
 * across sections (opening a bank closes an open expense).
 *
 * Usage: wrap a view in <OpenRowProvider>, then in a row call
 *   const [open, toggle] = useOpenRow('bank:' + id);
 * Keys are namespaced strings so two sections never collide.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { createContext, useCallback, useContext, useState } from 'react';

const Ctx = createContext(null);

export function OpenRowProvider({ children }) {
  const [openKey, setOpenKey] = useState(null);
  const toggle = useCallback((key) => setOpenKey((k) => (k === key ? null : key)), []);
  const open = useCallback((key) => setOpenKey(key), []);   // force-open a specific row
  const close = useCallback(() => setOpenKey(null), []);
  return <Ctx.Provider value={{ openKey, toggle, open, close }}>{children}</Ctx.Provider>;
}

// [isOpen, toggle] for a given namespaced key. Falls back to a private local
// state if used outside a provider, so a row is never dead.
export function useOpenRow(key) {
  const ctx = useContext(Ctx);
  const [localKey, setLocalKey] = useState(null);
  if (!ctx) {
    return [localKey === key, () => setLocalKey((k) => (k === key ? null : key))];
  }
  return [ctx.openKey === key, () => ctx.toggle(key)];
}

export function useAccordion() {
  return useContext(Ctx) || { openKey: null, toggle: () => {}, open: () => {}, close: () => {} };
}
